import React, { useEffect, useMemo, useState } from "react";
import { Form, Col, Row } from "react-bootstrap";
import Select, { components as selectComponents } from "react-select";

// "Ácme  HEALTH " -> "acme health"
const normalize = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Returns null to drop the option, or a tier number. Lower tiers sort first.
const scoreOption = (label, tokens, fullQuery) => {
  const haystack = normalize(label);
  if (!tokens.every((token) => haystack.includes(token))) return null; // must contain every word
  if (!fullQuery) return 4;                                            // empty query, keep original order
  if (haystack === fullQuery) return 0;                                // exact
  if (haystack.startsWith(fullQuery)) return 1;                        // "acme health kansas"
  if (new RegExp(`\\b${escapeRegExp(fullQuery)}`).test(haystack)) return 2; // "north acme health"
  return 3;                                                            // matched, but mid-word
};

const CustomSingleValue = (props) => (
  <selectComponents.SingleValue {...props} className="custom-singleValue" />
);

export const FilterDropdown = ({
  organizationList,
  onOrganizationChange,
  selected,
  autoFocus = false,
  placeholder = "Search organizations..."
}) => {
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [pendingSelection, setPendingSelection] = useState(null);

  const displayValue = pendingSelection || selected;

  useEffect(() => {
    setPendingSelection((pending) =>
      pending && selected && pending.value === selected.value ? null : pending
    );
  }, [selected]);

  // Let the value/placeholder wrap, and top-align so the caret sits on line 1.
  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: 38,
      height: "auto",
      alignItems: "flex-start"
    }),
    valueContainer: (provided) => ({
      ...provided,
      alignItems: "flex-start",
      overflow: "visible",
      paddingTop: 6,
      paddingBottom: 6
    }),
    singleValue: (provided) => ({
      ...provided,
      whiteSpace: "normal",
      overflow: "visible",
      textOverflow: "clip",
      wordBreak: "break-word",
      maxWidth: "100%",
      position: "relative",
      top: "auto",
      transform: "none"
    }),
    placeholder: (provided) => ({
      ...provided,
      whiteSpace: "normal",
      overflow: "visible",
      textOverflow: "clip",
      wordBreak: "break-word",
      maxWidth: "100%",
      position: "relative",
      top: "auto",
      transform: "none"
    }),
    input: (provided) => ({
      ...provided,
      margin: 0,
      paddingTop: 0,
      paddingBottom: 0
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      alignSelf: "stretch",
      alignItems: "center"
    }),
    menu: (provided) => ({
      ...provided,
      maxWidth: "120%"
    }),
    option: (provided) => ({
      ...provided,
      whiteSpace: "normal",
      wordBreak: "break-word"
    })
  };

  const visibleOptions = useMemo(() => {
    const list = organizationList || [];
    const fullQuery = normalize(inputValue);
    const tokens = fullQuery.split(/\s+/).filter(Boolean);

    const matched = [];
    list.forEach((option) => {
      const score = scoreOption(option && option.label, tokens, fullQuery);
      if (score !== null) matched.push({ option, score });
    });

    matched.sort((a, b) => a.score - b.score);

    return matched.map((entry) => entry.option);
  }, [organizationList, inputValue]);

  const handleInputChange = (nextValue, actionMeta) => {
    if (actionMeta.action === "input-change") {
      setInputValue(nextValue);
    } else if (actionMeta.action === "menu-close" || actionMeta.action === "input-blur") {
      setInputValue("");
    }
  };

  const handleChange = (option) => {
    setInputValue("");
    setIsFocused(false);
    setPendingSelection(option);
    onOrganizationChange(option);
  };

  const activePlaceholder =
    isFocused && displayValue && displayValue.label ? displayValue.label : placeholder;

  return (
    <Row className="align-items-center">
      <Col>
        <Form inline={true} className="mb-3" onSubmit={(event) => event.preventDefault()}>
          <Select
            className="form-select"
            isSearchable={true}
            autoFocus={autoFocus}
            openMenuOnFocus={true}
            blurInputOnSelect={true}
            controlShouldRenderValue={!isFocused}
            placeholder={activePlaceholder}
            value={displayValue}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            options={visibleOptions}
            filterOption={null}
            onChange={handleChange}
            components={{ SingleValue: CustomSingleValue }}
            styles={customStyles}
            maxMenuHeight={280}
            tabSelectsValue={false}
            noOptionsMessage={() => "No matching organizations"}
          />
        </Form>
      </Col>
    </Row>
  );
};